import React, { useEffect, useState } from 'react';
import AdViewer from '../components/ads/AdViewer';
import AdShare from '../components/ads/AdShare';
import { listVideos, trackAdView } from '../api/videos';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const DEFAULT_THUMBNAIL_URL =
  'https://via.placeholder.com/1200x600/1f2937/ffffff.png?text=TELLURVISION+Ad';

const isYouTubeUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    return host.includes('youtube.com') || host.includes('youtu.be');
  } catch {
    return false;
  }
};

const toYouTubeThumbnailUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    let videoId: string | null = null;

    if (host.includes('youtube.com')) {
      if (parsed.searchParams.has('v')) {
        videoId = parsed.searchParams.get('v');
      }
    } else if (host.includes('youtu.be')) {
      videoId = parsed.pathname.replace(/^\//, '') || null;
    }

    if (!videoId) return null;

    return `https://img.youtube.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
  } catch {
    return null;
  }
};

interface AdFromApi {
  _id: string;
  title: string;
  description: string;
  creativeType?: 'text' | 'image' | 'video';
  fundingStatus?: 'draft' | 'unfunded' | 'funded';
  videoUrl?: string;
  imageUrl?: string;
  categories?: string[];
  tags?: string[];
  views?: number;
  clicks?: number;
  createdAt?: string;
}

type AuthUserWithId = {
  _id?: string;
  user?: {
    _id?: string;
  };
};

type ListVideosResponse =
  | AdFromApi[]
  | {
      items: AdFromApi[];
      total?: number;
    }
  | null
  | undefined;

const PAGE_SIZE_DEFAULT = 6;

const Home: React.FC = () => {
  const { user } = useAuth();
  const [ads, setAds] = useState<AdFromApi[]>([]);
  const [selectedAd, setSelectedAd] = useState<AdFromApi | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE_DEFAULT);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Filter ads by search query
  const filteredAds = !searchQuery.trim()
    ? ads
    : ads.filter((ad) => {
        const q = searchQuery.trim().toLowerCase();
        return (
          (ad.title && ad.title.toLowerCase().includes(q)) ||
          (ad.description && ad.description.toLowerCase().includes(q)) ||
          (ad._id && ad._id.toLowerCase().includes(q))
        );
      });

  const totalViews = ads.reduce((sum, ad) => sum + (ad.views ?? 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks ?? 0), 0);
  const averageCtr = totalViews > 0 ? (totalClicks / totalViews) * 100 : null;

  const resolvedUserId: string | undefined = (() => {
    if (!user) return undefined;
    // login response shape is typically { token, user: { _id, ... } }
    const authUser = user as AuthUserWithId;
    if (authUser.user && typeof authUser.user._id === 'string') return authUser.user._id;
    if (typeof authUser._id === 'string') return authUser._id;
    return undefined;
  })();

  const isAdmin: boolean = (() => {
    if (!user) return false;
    const raw = user as Record<string, unknown> | null;
    const innerUser =
      raw && typeof raw === 'object' && 'user' in raw && raw.user && typeof raw.user === 'object'
        ? (raw.user as Record<string, unknown>)
        : raw;
    const role: string | undefined =
      innerUser && typeof innerUser.role === 'string' ? (innerUser.role as string) : undefined;
    return role === 'admin';
  })();

  useEffect(() => {
    let isCancelled = false;
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = (await listVideos({
          sortBy: 'createdAt',
          sortDir: 'desc',
          page,
          pageSize,
          includeMeta: true,
        })) as ListVideosResponse;
        if (isCancelled) return;

        if (data && !Array.isArray(data) && Array.isArray(data.items)) {
          setAds(data.items);
          setTotal(typeof data.total === 'number' ? data.total : null);
        } else if (Array.isArray(data)) {
          setAds(data);
          setTotal(null);
        } else {
          setAds([]);
          setTotal(null);
        }

        if (!selectedAd) {
          if (data && !Array.isArray(data) && Array.isArray(data.items) && data.items[0]) {
            setSelectedAd(data.items[0]);
          } else if (Array.isArray(data) && data[0]) {
            setSelectedAd(data[0]);
          }
        }
      } catch (err: unknown) {
        if (!isCancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load ads';
          setError(message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void fetchAds();

    return () => {
      isCancelled = true;
    };
  }, [
    resolvedUserId,
    page,
    pageSize,
    selectedAd,
    isAdmin,
  ]);

  const totalPages = total && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const handleSelectAd = (ad: AdFromApi) => {
    setSelectedAd(ad);
    void trackAdView(ad._id);
  };

  return (
    <main className="bg-slate-950">
      <div className="mx-auto max-w-6xl px-3 py-6 space-y-6 sm:px-4 sm:py-8 sm:space-y-8">
        <section className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-5 text-slate-50 shadow-lg sm:px-6 sm:py-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Welcome to TELLURVISION</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-200">
                We don't sell screen time. We sell attention in motion—every impression backed by
                live telemetry and analytics.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/campaign/start')}
                  className="inline-flex items-center rounded-md bg-blue-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  Start a campaign
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/payment')}
                  className="inline-flex items-center rounded-md border border-slate-500 bg-slate-900/40 px-3 py-1.5 text-xs font-medium text-slate-100 shadow-sm hover:bg-slate-800/70 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  Go to payments
                </button>
              </div>
            </div>
            {ads.length > 0 && (
              <div className="flex flex-wrap gap-3 text-xs lg:justify-end">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-800/80 px-3 py-1 font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Live campaigns: {total ?? ads.length}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-800/80 px-3 py-1 text-slate-200">
                  <span className="text-slate-300">Views</span>
                  <span className="font-semibold">{totalViews}</span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-800/80 px-3 py-1 text-slate-200">
                  <span className="text-slate-300">Clicks</span>
                  <span className="font-semibold">{totalClicks}</span>
                </span>
                {averageCtr !== null && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-800/80 px-3 py-1 text-slate-200">
                    <span className="text-slate-300">Avg CTR</span>
                    <span className="font-semibold">{averageCtr.toFixed(1)}%</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Payments entrypoint has moved to the My account page */}
        </section>

        {/* Find My Video search box */}
        <section className="flex flex-col gap-2 items-start mb-2">
          <label htmlFor="find-my-video" className="text-base font-medium text-slate-100">Find My Video</label>
          <input
            id="find-my-video"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, description, or reference ID..."
            className="w-full max-w-md rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </section>

        <section className="grid items-start gap-5 lg:grid-cols-3 lg:gap-6">
          <div className="order-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm lg:col-span-2 sm:p-4">
            <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">Selected Ad</h2>
            <AdViewer
              ad={
                selectedAd
                  ? {
                      title: selectedAd.title,
                      description: selectedAd.description,
                      creativeType: selectedAd.creativeType,
                      fundingStatus: selectedAd.fundingStatus,
                      videoUrl: selectedAd.videoUrl,
                      imageUrl: selectedAd.imageUrl,
                      categories: selectedAd.categories,
                      tags: selectedAd.tags,
                      views: selectedAd.views,
                      clicks: selectedAd.clicks,
                      motionData: {},
                    }
                  : undefined
              }
            />
            <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-3">
              <AdShare adId={selectedAd?._id} adTitle={selectedAd?.title} />
            </div>
          </div>

          <div className="order-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm sm:p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ad Feed</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Browse and filter active billboard creatives.
                </p>
              </div>
              {total !== null && (
                <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                  {total} ads
                </span>
              )}
            </div>

            {loading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading ads and live campaigns—one moment...</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="mt-2 max-h-[400px] overflow-y-auto pr-1 sm:max-h-[440px]">
              {filteredAds.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {filteredAds.map((ad) => (
                    <button
                      key={ad._id}
                      type="button"
                      onClick={() => handleSelectAd(ad)}
                      className="flex w-full cursor-pointer flex-col overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-left text-sm shadow-sm transition hover:border-blue-500 hover:bg-white hover:shadow-md"
                    >
                      <div className="aspect-video w-full overflow-hidden bg-black">
                        <img
                          src={
                            ad.imageUrl?.trim()
                              ? ad.imageUrl
                              : isYouTubeUrl(ad.videoUrl ?? undefined)
                                ? toYouTubeThumbnailUrl(ad.videoUrl as string) ||
                                  DEFAULT_THUMBNAIL_URL
                                : DEFAULT_THUMBNAIL_URL
                          }
                          alt={ad.title}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="flex flex-1 flex-col px-2 py-2">
                        <p className="line-clamp-2 text-xs font-semibold text-slate-900 dark:text-slate-100">
                          {ad.title}
                        </p>
                        <p className="mt-1 line-clamp-2 text-[11px] text-slate-600 dark:text-slate-400">
                          {ad.description}
                        </p>
                        <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                          <div className="flex items-center gap-1">
                            {typeof ad.views === 'number' &&
                              `${ad.views} view${ad.views === 1 ? '' : 's'}`}
                            {typeof ad.clicks === 'number' &&
                              ` • ${ad.clicks} click${ad.clicks === 1 ? '' : 's'}`}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                !loading &&
                !error && (
                  <p className="px-1 py-4 text-center text-xs text-slate-500 dark:text-slate-400">
                    No ads found yet. Create a new campaign to add one.
                  </p>
                )
              )}
            </div>

            {totalPages && totalPages > 1 && (
              <div className="mt-3 flex items-center justify-center gap-3 text-xs text-slate-600 dark:text-slate-400">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p + 1))}
                  className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Home;
